import { useState, useContext, useCallback, useMemo, useEffect } from "react";
import PollIcon from "../../assets/images/icons/poll-icon.svg";
import WebinarIcon from "../../assets/images/icons/webinar-icon.svg";
import ClinicalStudyIcon from "../../assets/images/icons/clinical-study-icon.svg";
import ClincalSessionIcon from "../../assets/images/icons/ic_clinical_sessions.svg";
import { useNavigate } from "react-router-dom";
import { Language, LoginUserData } from "../../App";
import { useCustomToast } from "../../components/common/CustomToast";

// Static data for post types
const PAGES = [
  { src: PollIcon, label: "poll", type: 2 },
  { src: WebinarIcon, label: "webinar", type: 3 },
  { src: ClinicalStudyIcon, label: "clinical_study", type: 4 },
  { src: ClincalSessionIcon, label: "clinical_session", type: 5 },
];

// Custom hook for PostForm logic
const usePostForm = ({ selectedPost, editWebinar, setEditWebinar, showPost, setShowPost, refetch ,handlePostSelect}) => {
  const { lang } = useContext(Language);
  const { splashData } = useContext(LoginUserData);
  const { showToast } = useCustomToast();
  const navigate = useNavigate();
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [titleMessage, setTitleMessage] = useState("");
  const [inviteWebinar, setInviteWebinar] = useState(false);
  const [inviteClinical, setInviteClinical] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [webinarId, setWebinarId] = useState("");
  const [clinicalId, setClinicalId] = useState("");

  // Set initial selectedLabel based on browser language
  useEffect(() => {
    setSelectedLabel(navigator.language.startsWith("en") ? "Publish" : "Publicar");
  }, []);

  // Memoize PAGES data
  const pages = useMemo(() => PAGES.map(page => ({
    ...page,
    label: lang?.[page.label] || page.label,
  })), [lang]);

  // Handle API call with progress and success/error handling
  const handleApiCall = useCallback(
    async (apiFunction, { type }) => {
      const benefitKeyByType = {
        post: "upload_post",
        poll: "upload_poll",
        webinar: "upload_webinar",
        "clinical-study": "create_clinical_study",
        "clinical-session": "create_clinical_session",
      };

      const requiredBenefitKey = benefitKeyByType[type];
      if (requiredBenefitKey) {
        const splashStorage = JSON.parse(localStorage.getItem("SplashData") || "{}");
        const signupStorage = JSON.parse(localStorage.getItem("SignupData") || "{}");
        const userType = splashStorage?.user?.user_type || signupStorage?.data?.user_type;
        const activeSubscription =
          splashStorage?.active_subscription ?? signupStorage?.active_subscription ?? null;
        const benefits = Array.isArray(splashStorage?.benefits)
          ? splashStorage.benefits
          : Array.isArray(signupStorage?.benefits)
            ? signupStorage.benefits
            : [];

        // Restrict only for industry users with active subscription.
        if (userType === "industry" && activeSubscription) {
          const requiredBenefit = benefits.find(
            (b) =>
              String(b?.benefit_key || "").trim().toLowerCase() ===
              String(requiredBenefitKey).trim().toLowerCase()
          );
          const isAllowed =
            requiredBenefit?.is_enabled === true ||
            requiredBenefit?.is_enabled === 1 ||
            requiredBenefit?.is_enabled === "1" ||
            requiredBenefit?.is_enabled === "true";

          if (!isAllowed) {
            showToast("This action is not allowed in your current subscription.", "error");
            return;
          }
        }
      }

      const typeMessages = {
        poll: lang.uploading_your_poll,
        webinar: lang.uploading_your_webinar,
        "clinical-session": lang.uploading_your_clinical_session,
        post: lang.uploading_your_post,
        "clinical-study": lang.uploading_your_clinical_study,
      };

      setTitleMessage(typeMessages[type] || lang.uploading_your_post);
      setShowProgress(true);

      try {
        const response = await apiFunction();
        if (response) {
          setShowProgress(false);
          if (type === "webinar" && response.data.post.id) {
            setWebinarId(response.data.post.id);
            setInviteWebinar(true);
          } else if (type === "clinical-session" && response.data.post.id) {
            setClinicalId(response.data.post.id);
            setInviteClinical(true);
          }
          if (type !== "clinical-study") {
            refetch();
          }
          setProgress(0);
        } else {
          setShowProgress(false);
          console.error("Unexpected response");
        }
      } catch (error) {
        setShowProgress(false);
        console.error(error);
      }
    },
    [lang, refetch, showToast]
  );

  // Handle label click
  const handleLabelClick = useCallback((label) => {
    setSelectedLabel(label);
  }, []);

  // Handle form click
  const handleFormClick = useCallback(() => {
    setShowPost(true);
  }, [setShowPost]);

  // Handle publish button click
  const handlePublishClick = useCallback(() => {
    setSelectedLabel(lang.publish);
  }, [lang]);

  return {
    lang,
    splashData,
    navigate,
    selectedLabel,
    titleMessage,
    inviteWebinar,
    inviteClinical,
    showProgress,
    progress,
    webinarId,
    clinicalId,
    pages,
    setInviteWebinar,
    setInviteClinical,
    setShowProgress,
    setProgress,
    handleApiCall,
    handleLabelClick,
    handleFormClick,
    handlePublishClick,
    selectedPost,
    editWebinar,
    setEditWebinar,
    showPost,
    setShowPost,
    refetch,
    handlePostSelect
  };
}
export default usePostForm